import {
  ElementNode,
  type EditorConfig,
  type LexicalNode,
  type LexicalUpdateJSON,
  type NodeKey,
  type SerializedElementNode,
  type Spread,
} from "lexical";

import {
  PRODUCT_BLOCK_CLOSE_MARKER,
  productBlockOpenMarker,
} from "../productBlocks";

export type SerializedProductBlockNode = Spread<
  { index: number },
  SerializedElementNode
>;

export const PRODUCT_BLOCK_CLASS = "lexical-product-block";
export const PRODUCT_BLOCK_HEADER_CLASS = "lexical-product-block-header";

/**
 * A block of copy bound to one product, by absolute index. Everything inside
 * is dropped together when the tile has fewer products, so the author decides
 * what belongs to the product instead of the transform guessing from line
 * breaks.
 *
 * Serialises to the marker pair in {@link productBlocks}, which is what lands
 * in `bodyText`; `ProductBlockPlugin` rebuilds the node from those markers on
 * load. The index is positional — `$reindexProductBlocks` keeps it (and the
 * chips inside) honest as blocks and contexts come and go, so this node never
 * rewrites its own content at serialize time.
 */
export class ProductBlockNode extends ElementNode {
  __index: number;

  static getType(): string {
    return "product-block";
  }

  static clone(node: ProductBlockNode): ProductBlockNode {
    return new ProductBlockNode(node.__index, node.__key);
  }

  static importJSON(
    serializedNode: SerializedProductBlockNode,
  ): ProductBlockNode {
    return new ProductBlockNode(serializedNode.index ?? 0).updateFromJSON(
      serializedNode,
    );
  }

  constructor(index: number, key?: NodeKey) {
    super(key);
    this.__index = index;
  }

  updateFromJSON(
    serializedNode: LexicalUpdateJSON<SerializedProductBlockNode>,
  ): this {
    return super.updateFromJSON(serializedNode);
  }

  exportJSON(): SerializedProductBlockNode {
    return { ...super.exportJSON(), index: this.__index };
  }

  createDOM(config: EditorConfig): HTMLElement {
    const div = document.createElement("div");
    div.className = PRODUCT_BLOCK_CLASS;
    div.setAttribute("data-product-index", String(this.__index));
    if (config.theme.paragraph) div.classList.add(config.theme.paragraph);

    // A plain label, not a button: the index is positional (the reindex pass
    // owns it), so there is nothing to configure on a block — only delete.
    const header = document.createElement("span");
    header.className = PRODUCT_BLOCK_HEADER_CLASS;
    header.setAttribute("contenteditable", "false");

    const label = document.createElement("span");
    label.className = "lexical-product-header-label";
    label.textContent = `Product ${this.__index + 1}`;

    const remove = document.createElement("span");
    remove.className = "lexical-product-delete";
    remove.setAttribute("role", "button");
    remove.setAttribute("aria-label", "Delete product");
    remove.textContent = "✕";

    header.append(label, remove);
    div.appendChild(header);

    return div;
  }

  /** Keeps the header out of the managed children Lexical reconciles. */
  getDOMSlot(element: HTMLElement) {
    return super.getDOMSlot(element).withAfter(element.firstChild);
  }

  updateDOM(prevNode: ProductBlockNode, dom: HTMLElement): boolean {
    if (prevNode.__index === this.__index) return false;
    dom.setAttribute("data-product-index", String(this.__index));
    const label = dom.firstChild?.firstChild;
    if (label instanceof HTMLElement) {
      label.textContent = `Product ${this.__index + 1}`;
    }
    return false;
  }

  /**
   * Whole lines, so the marker pair round-trips through `bodyText` as an
   * ordinary line scan rather than needing offsets into a flat string.
   *
   * Children are paragraphs — one per line — so they join with `\n` rather than
   * the `\n\n` Lexical's own `ElementNode.getTextContent` would insert.
   */
  getTextContent(): string {
    const inner = this.getChildren()
      .map((child) => child.getTextContent())
      .join("\n");
    return [
      productBlockOpenMarker(this.__index),
      inner,
      PRODUCT_BLOCK_CLOSE_MARKER,
    ].join("\n");
  }

  getIndex(): number {
    return this.getLatest().__index;
  }

  setIndex(index: number): this {
    const writable = this.getWritable();
    writable.__index = index;
    return writable;
  }

  /**
   * Backspace never dissolves the block — that would leak its product chips
   * into looser scopes. The structural two-step in `ProductBlockPlugin`
   * (select the wrapper, then delete) owns its lifecycle.
   */
  collapseAtStart(): boolean {
    return true;
  }

  canBeEmpty(): boolean {
    return false;
  }

  isInline(): boolean {
    return false;
  }

  /** Holds paragraphs, so Lexical treats it as a container rather than a leaf. */
  isShadowRoot(): boolean {
    return false;
  }
}

export function $createProductBlockNode(index: number): ProductBlockNode {
  return new ProductBlockNode(index);
}

export function $isProductBlockNode(
  node: LexicalNode | null | undefined,
): node is ProductBlockNode {
  return node instanceof ProductBlockNode;
}
